import { memo } from 'react';
import { AlertBanner, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Mail } from 'lucide-react';

import { formatBatchLabel } from '../../utils/formatBatchLabel.js';

/**
 * Banner of the Members list while it is filtered to an import batch: the batch name, how many
 * people it added and how many are not invited yet, with Undo import (only while nobody was
 * invited) and "Select the N not invited".
 *
 * @param {object} props
 * @param {{ date: string, file: string, memberCount: number, notInvited: number }} props.batch
 * @param {() => void} props.onSelectNotInvited
 * @param {() => void} props.onUndo
 */
function ImportedBatchBanner({ batch, onSelectNotInvited, onUndo }) {
  const { t, i18n } = useTranslation('people-access');
  const { memberCount, notInvited } = batch;
  // An email cannot be recalled: once someone of the batch was invited the import stays.
  const canUndo = memberCount > 0 && notInvited === memberCount;

  return (
    <AlertBanner
      tone="info"
      icon={Mail}
      title={formatBatchLabel(t, i18n.language, new Date(batch.date), batch.file)}
      action={
        <>
          {canUndo ? (
            <Button variant="tertiary" size="sm" onClick={onUndo}>
              {t('members.importBatch.undo')}
            </Button>
          ) : null}
          {notInvited > 0 ? (
            <Button size="sm" onClick={onSelectNotInvited}>
              {t('members.importBatch.selectNotInvited', { count: notInvited })}
            </Button>
          ) : null}
        </>
      }
    >
      {notInvited > 0
        ? t('members.importBatch.banner', { count: memberCount, notInvited })
        : t('members.importBatch.allInvited', { count: memberCount })}
    </AlertBanner>
  );
}

export default memo(ImportedBatchBanner);
